import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import ModalCobro from '../components/ModalCobro';
import Ticket from '../components/Ticket';

const CATEGORIAS = [
  { value: 'todas',           label: '🗂️ Todas' },
  { value: 'pollo',           label: '🍗 Pollo' },
  { value: 'mariscos',        label: '🦐 Mariscos' },
  { value: 'carnes',          label: '🥩 Carnes' },
  { value: 'sopas',           label: '🍜 Sopas' },
  { value: 'especiales',      label: '⭐ Especiales' },
  { value: 'acompañamientos', label: '🍚 Acompañamientos' },
  { value: 'bebida',          label: '🥤 Bebidas' },
];

export default function Comedor() {
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [categoriaActiva, setCategoriaActiva] = useState('todas');
  const [orden, setOrden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModalCobro, setShowModalCobro] = useState(false);
  const [saleResult, setSaleResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    cargarProductos();
  }, []);

  const cargarProductos = async () => {
    try {
      const res = await api.get('/restaurante/productos');
      setProductos(res.data);
    } catch (error) {
      Swal.fire('Error', 'No se pudieron cargar los productos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const agregarProducto = (producto) => {
    setOrden(prev => {
      const existente = prev.find(i => i.id === producto.id);
      if (existente) {
        return prev.map(i =>
          i.id === producto.id
            ? { ...i, cantidad: i.cantidad + 1, subtotal: (i.cantidad + 1) * parseFloat(i.precio_venta) }
            : i
        );
      }
      return [...prev, { ...producto, cantidad: 1, subtotal: parseFloat(producto.precio_venta) }];
    });
  };

  const quitarProducto = (productoId) => {
    setOrden(prev => {
      const existente = prev.find(i => i.id === productoId);
      if (existente.cantidad === 1) {
        return prev.filter(i => i.id !== productoId);
      }
      return prev.map(i =>
        i.id === productoId
          ? { ...i, cantidad: i.cantidad - 1, subtotal: (i.cantidad - 1) * parseFloat(i.precio_venta) }
          : i
      );
    });
  };

  const eliminarProducto = (productoId) => {
    setOrden(prev => prev.filter(i => i.id !== productoId));
  };

  const limpiarOrden = () => {
    setOrden([]);
  };

  const total = orden.reduce((acc, i) => acc + i.subtotal, 0);

  const itemsParaCobro = orden.map(item => ({
    uniqueKey: `${item.id}-${Date.now()}`,
    product_id: item.id,
    presentation_id: null,
    codigo: item.codigo || `PROD-${item.id}`,
    nombre: item.nombre,
    precio_unitario: parseFloat(item.precio_venta),
    cantidad: item.cantidad,
    subtotal: item.subtotal,
    gravado: Boolean(item.aplica_iva),
  }));

  const facturar = () => {
    if (orden.length === 0) {
      Swal.fire('Aviso', 'Agrega al menos un producto', 'warning');
      return;
    }
    setShowModalCobro(true);
  };

  const productosFiltrados = productos.filter(p => {
    const coincideBusqueda = p.nombre.toLowerCase().includes(busqueda.toLowerCase());
    const coincideCategoria = categoriaActiva === 'todas' || p.categoria === categoriaActiva;
    return coincideBusqueda && coincideCategoria;
  });

  const getFotoUrl = (fotoPath) => {
    if (!fotoPath) return null;
    if (fotoPath.startsWith('http')) return fotoPath;
    return `https://restaurante.altivaweb.com/BACK/public/storage/${fotoPath}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="p-3" style={{ height: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">🍽️ Comedor</h4>
        <strong>Total: ${total.toFixed(2)}</strong>
      </div>

      <div className="row h-100">
        {/* Productos */}
        <div className="col-7 border-end">
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {CATEGORIAS.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setCategoriaActiva(cat.value)}
                style={{
                  padding: '5px 12px',
                  borderRadius: '16px',
                  border: '2px solid',
                  borderColor: categoriaActiva === cat.value ? '#667eea' : '#e2e8f0',
                  background: categoriaActiva === cat.value ? '#667eea' : 'white',
                  color: categoriaActiva === cat.value ? 'white' : '#4a5568',
                  fontWeight: 600,
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <input
            type="text"
            className="form-control mb-3"
            placeholder="Buscar porción..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />

          <div className="row g-2" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            {productosFiltrados.map((producto) => (
              <div key={producto.id} className="col-4">
                <div
                  className="card text-center"
                  style={{
                    cursor: 'pointer',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    transition: 'transform 0.1s',
                  }}
                  onClick={() => agregarProducto(producto)}
                >
                  {producto.foto ? (
                    <img
                      src={getFotoUrl(producto.foto)}
                      alt={producto.nombre}
                      style={{ width: '100%', height: '80px', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{
                      width: '100%', height: '80px', background: '#f7fafc',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px',
                    }}>
                      🍽️
                    </div>
                  )}
                  <div className="p-2">
                    <small className="fw-bold d-block" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                      {producto.nombre}
                    </small>
                    <small className="text-success fw-bold d-block" style={{ marginTop: '4px' }}>
                      ${parseFloat(producto.precio_venta).toFixed(2)}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Orden actual */}
        <div className="col-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Orden actual</h6>
            {orden.length > 0 && (
              <button className="btn btn-sm btn-outline-danger" onClick={limpiarOrden}>
                🗑️ Limpiar
              </button>
            )}
          </div>

          {orden.length === 0 ? (
            <p className="text-muted text-center mt-4">No hay productos agregados</p>
          ) : (
            <>
              <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {orden.map((item) => (
                  <div key={item.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                    <div style={{ flex: 1 }}>
                      <small className="fw-bold d-block">{item.nombre}</small>
                      <small className="text-muted">${parseFloat(item.precio_venta).toFixed(2)} c/u</small>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: '2px 8px', fontSize: '14px' }}
                        onClick={() => quitarProducto(item.id)}
                      >−</button>
                      <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: 600 }}>
                        {item.cantidad}
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: '2px 8px', fontSize: '14px' }}
                        onClick={() => agregarProducto(item)}
                      >+</button>
                      <span className="fw-bold ms-2" style={{ minWidth: '60px', textAlign: 'right' }}>
                        ${item.subtotal.toFixed(2)}
                      </span>
                      <button
                        className="btn btn-sm btn-outline-danger ms-1"
                        style={{ padding: '2px 6px', fontSize: '12px' }}
                        onClick={() => eliminarProducto(item.id)}
                      >✕</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-top pt-2 mt-2">
                <div className="d-flex justify-content-between mb-3">
                  <strong>Total:</strong>
                  <strong style={{ fontSize: '18px', color: '#38a169' }}>${total.toFixed(2)}</strong>
                </div>
                <button className="btn btn-success w-100" onClick={facturar}>
                  💰 Crear Venta
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showModalCobro && (
        <ModalCobro
          items={itemsParaCobro}
          total={total}
          onSuccess={(data) => {
            setSaleResult(data);
            setShowModalCobro(false);
            setOrden([]);
          }}
          onCancel={() => setShowModalCobro(false)}
        />
      )}

      {saleResult && (
        <Ticket
          invoice={saleResult}
          onAfterPrint={() => setSaleResult(null)}
        />
      )}
    </div>
  );
}